import {
  type DocumentTabType,
  type ModalId,
  TemplateCategory,
  type TemplateType,
} from '@heidi/schema/ml-scribe';
// Top-level `import type`, deliberately not an inline `{ type X }`: the V3
// contracts-inertness fixture only sanctions this cross-package pick when it is
// provably type-only, and an inline specifier does not carry that at the
// import-statement level.
import type { PatientSummaryTurnOffPromptState } from '@heidi/workspace/contracts';

/**
 * Temporary string-literal stubs for the two enum members that the backend
 * hasn't shipped yet. `TemplateCategory.PATIENT_SUMMARY_TEMPLATE` is already
 * in the generated schema and is re-exported below; remove the remaining
 * stubs and consumers should import the canonical enum members directly once
 * the BE ships them.
 */

export const PATIENT_SUMMARY_DOCUMENT_TAB_TYPE = 'PATIENT_SUMMARY' as DocumentTabType;

export const PATIENT_SUMMARY_TEMPLATE_CATEGORY =
  TemplateCategory.PATIENT_SUMMARY_TEMPLATE;

export const PATIENT_SUMMARY_TEMPLATE_TYPE = 'PATIENT_SUMMARY' as TemplateType;

// PatientSummaryTurnOffPromptState moved to `@heidi/workspace/contracts`
// (Wave 2 · W, ARCFE-801 — it rides the user record's contract); re-exported
// here for existing importers until the Z-series codemod.
export type { PatientSummaryTurnOffPromptState };

/** Safe default when the backend hasn't merged the field yet (or it's absent). */
export const DEFAULT_PATIENT_SUMMARY_TURN_OFF_PROMPT_STATE: PatientSummaryTurnOffPromptState =
  {
    dismissal_count: 0,
    last_dismissed_session_id: null,
    seen: false,
  };

/** Distinct-session dismissals required before the turn-off prompt is shown. */
export const PATIENT_SUMMARY_DISMISSAL_THRESHOLD = 3;

/**
 * Modal-queue id for the turn-off prompt. Cast like the coachmark's id — the backend
 * `ModalId` enum entry is optional here because persistence uses the
 * `patient_summary_turn_off_prompt.seen` field, not `seen_modals`.
 *
 * Lives with the types rather than beside the hook so the record-dismissal mutation can
 * enqueue the prompt without importing the hook that consumes the mutation.
 */
export const PATIENT_SUMMARY_TURN_OFF_PROMPT_MODAL_ID =
  'PATIENT_SUMMARY_TURN_OFF_PROMPT' as ModalId;

/**
 * Request for the atomic record-dismissal endpoint
 * (`POST /api/v1/ml-scribe/user/patient-summary-turn-off-prompt:record-dismissal`).
 */
export interface RecordPatientSummaryDismissalRequest {
  session_id: string;
}

/**
 * Response from the atomic record-dismissal endpoint. The server is authoritative
 * for the distinct-session count / dedupe / seen logic (atomic `$inc`), so it
 * returns the updated state plus whether the turn-off prompt should now be shown.
 */
export interface RecordPatientSummaryDismissalResponse {
  patient_summary_turn_off_prompt: PatientSummaryTurnOffPromptState;
  should_prompt: boolean;
}

/**
 * Request/response shapes for the v2 patient-summary delivery endpoints.
 *
 * Lives in `src/types/` (not `pages/api/scribeAPIV2.ts`) so atoms, hooks, and
 * the API client can all import the types without forming a circular
 * dependency through `scribeAPIV2.ts` (which itself depends on a large slice
 * of the app via its surrounding helpers).
 */
export interface PreparePatientSummaryDeliveryRequest {
  document_id: string;
  patient_profile_id: string;
  /** E.164 formatted phone number, e.g. "+447933994683". */
  delivery_phone: string;
  /** Optional short line (max 500 chars) prepended to the SMS body on send. */
  patient_message?: string | null;
}

export interface PreparePatientSummaryDeliveryResponse {
  delivery_token: string;
  delivery_token_expires_at: string;
  /** Direct presigned S3 URL to the encrypted PDF (used for downloads). */
  download_url: string;
  /**
   * Fully-formed proxy URL with the delivery token already in the query
   * string. Safe to drop straight into an `<iframe>`/`window.open` — the
   * backend serves the PDF without the client needing to hit S3 directly.
   * The PDF itself is still password-protected with the patient's DOB.
   */
  preview_pdf_url: string;
  file_key: string;
  pdf_expires_at: string;
  /**
   * Patient's DOB-derived password used to unlock the encrypted PDF in the
   * clinician's in-app preview, or `null` when the backend ships an
   * unencrypted PDF (e.g. partial prepare responses or non-DOB regions).
   * PHI: never persist beyond the lifetime of the active delivery flow
   * and never log.
   */
  password: string | null;
  /** Localized human-readable hint that explains the password format. */
  password_hint: string;
  /**
   * Localized programmatic instruction the clinician's chat composer used to
   * tell the Evidence backend to dispatch the SMS (e.g. "Heidi, send this
   * patient summary to +447…"). Retained on the response so the chat surface
   * can render the bolded trigger phrase verbatim when a clinician asks
   * Heidi to send a summary via natural language; the chip-driven direct
   * dispatch path (the default in the FE today) does not use this field.
   */
  instructions_for_clinician: string;
  /**
   * Localized SMS body the patient will receive verbatim — the clinician
   * sees this in the confirm dialog before hitting Send. Read-only on the
   * client today; use this rather than reconstructing the body locally so
   * the preview never diverges from what the backend actually dispatches.
   */
  sms_body_preview: string;
  /**
   * The greeting line the clinician can edit. Echoes back a submitted
   * `patient_message`, or the localized default greeting when none was
   * sent. Seed the editable message field with this rather than parsing
   * it out of `sms_body_preview` (the body's fixed link/password/opt-out
   * tail is appended by the backend and must not be editable).
   */
  editable_message: string;
  /**
   * The short proxy download URL that appears verbatim inside
   * `sms_body_preview`. Returned separately so the preview can locate it
   * in the body and render it as a chip without re-templating the copy.
   */
  sms_short_url: string;
  delivery_phone: string;
  patient_name: string;
  clinician_name: string;
}

/**
 * Request shape for the dispatch endpoint. Same as the prepare request —
 * the backend re-runs validation server-side before triggering Temporal,
 * so the FE never has to trust a cached preview between dialog open and
 * confirm click.
 */
export type SendPatientSummaryDeliveryRequest = PreparePatientSummaryDeliveryRequest;

/**
 * Response shape for `POST /patient-summary-delivery/send`.
 *
 * `success=true` paths set `message_sid` + `status` (Twilio confirmation)
 * and leave `error` / `error_reason` null. `success=false` paths populate
 * `error` (toast copy) and `error_reason` (machine-readable: e.g.
 * `comms_not_available_in_region`, `workflow_already_started`,
 * `comms_send_failed`) for logging and routing the FE retry affordance.
 *
 * Deterministic transport failures return 200 with `success=false` so the
 * confirm dialog can render an inline error + retry button; reserved
 * non-2xx for caller-fixable failures (validation, missing fields, the
 * feature flag being off).
 */
export interface SendPatientSummaryDeliveryResponse {
  success: boolean;
  delivery_phone: string;
  patient_name: string;
  clinician_name: string;
  password: string | null;
  password_hint: string;
  download_url: string;
  /** Full SMS body the BE dispatched (or would have dispatched on retry). */
  sms_body: string;
  message_sid?: string | null;
  status?: string | null;
  error?: string | null;
  error_reason?: string | null;
}

/**
 * The send's source, encoded so exactly one id can be supplied — a payload
 * carrying both or neither is a type error, mirroring the backend's 400 on
 * `bool(document_id) == bool(session_id)`. For a PHI-delivery request the
 * compiler enforcing the exclusivity is worth the union's extra shape.
 */
type SendPatientSummaryEmailSource =
  /** Saved document to send. */
  | { document_id: string; session_id?: null }
  /** Session whose consult note to send, when not sourced from a saved document. */
  | { session_id: string; document_id?: null };

/**
 * Request for the M0 email delivery of a patient-summary PDF (email-only path).
 *
 * Unlike the SMS `send` endpoint, this does not require a linked patient
 * profile: the clinician can supply the recipient details inline. When
 * `patient_profile_id` is present the backend may also back-fill the profile.
 * The endpoint prepares the password-protected PDF (password derived from
 * `last_name` + `birth_date`) and emails a secure download link.
 */
export type SendPatientSummaryEmailRequest = SendPatientSummaryEmailSource & {
  /** Linked patient profile id, when the session has one; null for ad-hoc sends. */
  patient_profile_id?: string | null;
  /** Recipient email address. */
  delivery_email: string;
  /** Patient last name — part of the DOB-derived PDF password. */
  last_name: string;
  /** Patient date of birth, ISO `yyyy-MM-dd` — part of the PDF password. */
  birth_date: string;
  /** Optional clinician greeting prepended to the email body. */
  patient_message?: string | null;
  /**
   * Patient-facing document *type* (e.g. "visit summary", "document") used in
   * the email subject + body. Deliberately NOT the document's file name — the
   * file name can carry clinician-entered PHI, so compliance requires a
   * constrained type label here (the backend additionally clamps it to an
   * allowlist, defaulting to "document").
   */
  document_type: string;
};

/**
 * Response for `POST /patient-summary-delivery/send-email`.
 *
 * Mirrors {@link SendPatientSummaryDeliveryResponse}: deterministic transport
 * failures resolve to 200 with `success=false` + `error` (toast copy) /
 * `error_reason` (machine-readable), while caller-fixable failures (validation,
 * missing fields, feature flag off) arrive as thrown 4xx errors.
 */
export interface SendPatientSummaryEmailResponse {
  success: boolean;
  delivery_email: string;
  patient_name: string;
  clinician_name: string;
  download_url: string;
  error?: string | null;
  error_reason?: string | null;
}

/**
 * What a send or preview renders: a saved document, or a session's consult
 * note. Modelled as a union rather than two optional ids so a call site can't
 * express "both" or "neither" — the backend rejects those anyway.
 */
export type PatientSummarySource = { documentId: string } | { sessionId: string };

/**
 * Response for `GET /patient-summary-delivery/{document_id}/email-preview`
 * and its note-sourced sibling `…/note/{session_id}/email-preview`.
 *
 * The backend is the single source of truth for the patient email copy: the
 * same renderers produce this preview and the actually-sent email, so the two
 * can never drift. `body_text` is the localized (clinician's request locale),
 * name-free plain-text body the clinician previews read-only; `link_placeholder`
 * is the token inside `body_text` the FE turns into the clickable "Open
 * document" preview link (the real URL only exists at send time).
 */
export interface PatientSummaryEmailPreviewResponse {
  subject: string;
  body_text: string;
  link_placeholder: string;
}
